/**
 * SharePostRow - "Share this article" section rendered above Related Posts.
 *
 * Design: dark slate-900 band that forms a unified "end-of-article" zone
 * with the RelatedPosts section below it. The two sections share the same
 * background so they read as one editorial block.
 *
 * Features:
 * - Dynamic hero thumbnail preview card (mimics social link preview)
 * - Full-bleed dark band (-mx negative margin to escape article column)
 * - Large icon-forward share buttons with brand colours
 * - Copy-link with clipboard feedback
 * - Native share API on mobile
 * - GA4 custom event: share_click { platform, post_slug }
 * - Arabic mode: detects googtrans cookie, swaps headline + share copy
 * - Accessible: aria-labels, focus rings, active:scale-95 press feedback
 */

import { useState, useEffect } from "react";
import { Linkedin, Twitter, Link2, Check, Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { GooglePreferredSourceButton } from "@/components/GooglePreferredSourceButton";
import { trackOutboundSocialClick, trackBlogShareClick } from "@/lib/analytics";
import { ResponsiveImage } from "@/components/ResponsiveImage";

interface SharePostRowProps {
  slug?: string;
  title: string;
  excerpt?: string;
  heroImage?: string;
  canonicalUrl?: string;
}

const BASE_URL = "https://integrated.social";

// ── Arabic copy ───────────────────────────────────────────────────────────────
const AR = {
  headline: "وجدت هذا مفيداً؟ شاركه مع شبكتك.",
  subCopy: "ساعد زميلاً على مواكبة عالم التسويق بالذكاء الاصطناعي.",
  linkedInCaption: (title: string, excerpt?: string) =>
    `${title}${excerpt ? ` - ${excerpt}` : ""}\n\nمن Integrated.Social - وكالة التسويق بالذكاء الاصطناعي وتحسين محركات البحث بالذكاء الاصطناعي (AEO/GEO/LLMO) في لندن.`,
  whatsappText: (title: string, url: string) =>
    `${title} - من Integrated.Social: ${url}`,
};

function isArabicActive(): boolean {
  if (typeof document === "undefined") return false;
  const match = document.cookie.match(/googtrans=([^;]+)/);
  if (!match) return false;
  return decodeURIComponent(match[1]).includes("/ar");
}

function trackShare(platform: string, slug: string, title: string) {
  // Typed helper - fires blog_share_click with full dimensions
  trackBlogShareClick({
    platform,
    post_slug: slug,
    post_title: title,
    share_method: platform === "native_share" ? "native_api" : "icon_button",
  });
  // Also fire outbound social click for non-copy/native platforms
  if (platform !== "copy_link" && platform !== "native_share") {
    trackOutboundSocialClick({
      social_platform: platform,
      link_location: "blog_share_row",
      destination_url: `https://integrated.social/blog/${slug}/`,
    });
  }
}

// ── WhatsApp SVG icon ─────────────────────────────────────────────────────────
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// ── Dynamic hero thumbnail preview card ───────────────────────────────────────
function SharePreviewCard({
  title,
  excerpt,
  heroImage,
  canonicalUrl,
}: {
  title: string;
  excerpt?: string;
  heroImage?: string;
  canonicalUrl: string;
}) {
  const domain = "integrated.social";
  const displayExcerpt = excerpt
    ? excerpt.length > 120
      ? excerpt.slice(0, 117) + "..."
      : excerpt
    : null;

  return (
    <a
      href={canonicalUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Preview: ${title}`}
      className="group flex flex-col sm:flex-row overflow-hidden rounded-xl border border-white/10
        bg-white/5 hover:bg-white/8 hover:border-white/20 transition-all duration-200
        max-w-2xl mb-7 no-underline"
    >
      {/* Hero thumbnail */}
      {heroImage && (
        <div className="sm:w-48 sm:flex-shrink-0 h-36 sm:h-auto overflow-hidden bg-slate-800">
          <ResponsiveImage
            src={heroImage.startsWith("http") ? heroImage : `${BASE_URL}${heroImage}`}
            alt={title}
            width={640}
            height={360}
            sizes="(min-width: 640px) 192px, calc(100vw - 32px)"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
      )}

      {/* Text content */}
      <div className="flex flex-col justify-between p-4 min-w-0">
        {/* Domain badge */}
        <div className="flex items-center gap-1.5 mb-2">
          <div className="w-3.5 h-3.5 rounded-sm bg-amber-400 flex-shrink-0" aria-hidden="true" />
          <span className="text-xs font-medium text-white/40 uppercase tracking-wider">{domain}</span>
          <ExternalLink className="w-3 h-3 text-white/30 ml-auto flex-shrink-0" aria-hidden="true" />
        </div>

        {/* Title */}
        <p className="text-sm font-semibold text-white leading-snug line-clamp-2 mb-1.5 group-hover:text-amber-300 transition-colors">
          {title}
        </p>

        {/* Excerpt */}
        {displayExcerpt && (
          <p className="text-xs text-white/50 leading-relaxed line-clamp-2">
            {displayExcerpt}
          </p>
        )}
      </div>
    </a>
  );
}

// ── Copy link button ──────────────────────────────────────────────────────────
function CopyLinkBtn({ url, slug, title }: { url: string; slug: string; title: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    trackShare("copy_link", slug, title);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <button
      onClick={handleCopy}
      aria-label={copied ? "Link copied" : "Copy article link"}
      title={copied ? "Copied!" : "Copy link"}
      className={`w-9 h-9 rounded-full flex items-center justify-center
        border transition-all duration-200 active:scale-90 focus-visible:outline-none
        focus-visible:ring-2 focus-visible:ring-amber-400
        ${copied
          ? "border-green-400 bg-green-400/15 text-green-400"
          : "border-white/20 bg-white/5 hover:bg-amber-400/15 hover:border-amber-400 text-white/70 hover:text-amber-400"
        }`}
    >
      {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
    </button>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function SharePostRow({
  slug,
  title,
  excerpt,
  heroImage,
  canonicalUrl: propCanonicalUrl,
}: SharePostRowProps) {
  const [arabic, setArabic] = useState(false);

  useEffect(() => {
    setArabic(isArabicActive());
  }, []);

  const canonicalUrl = propCanonicalUrl ?? `${BASE_URL}/blog/${slug ?? ""}/`;
  const pageId = slug ?? canonicalUrl.replace(BASE_URL, "").replace(/\//g, "-").replace(/^-|-$/g, "");
  const encodedUrl   = encodeURIComponent(canonicalUrl);
  const encodedTitle = encodeURIComponent(title);
  const trackId      = pageId;

  // Build absolute hero image URL for platforms that accept an image param
  const absoluteHeroImage = heroImage
    ? heroImage.startsWith("http")
      ? heroImage
      : `${BASE_URL}${heroImage}`
    : null;

  // LinkedIn share URL - LinkedIn reads og:image from the page itself (no image param in share URL)
  const linkedInUrl = arabic
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodeURIComponent(AR.linkedInCaption(title, excerpt))}`
    : (() => {
        const summary = excerpt
          ? encodeURIComponent(excerpt.length > 200 ? excerpt.slice(0, 197) + "..." : excerpt)
          : "";
        return summary
          ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${summary}`
          : `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      })();

  // X/Twitter - supports via, text, url
  const twitterText = excerpt
    ? encodeURIComponent(`${title} - ${excerpt.slice(0, 100)}`)
    : encodedTitle;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${twitterText}&via=IntegratedSocial`;

  // WhatsApp - include title + excerpt + URL for rich preview
  const whatsappUrl = arabic
    ? `https://wa.me/?text=${encodeURIComponent(AR.whatsappText(title, canonicalUrl))}`
    : `https://wa.me/?text=${encodeURIComponent(excerpt
        ? `${title}\n\n${excerpt.slice(0, 150)}\n\n${canonicalUrl}`
        : `${title}\n\n${canonicalUrl}`
      )}`;

  const hasNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleNativeShare = async () => {
    try {
      const shareData: ShareData = { title, url: canonicalUrl };
      if (excerpt) shareData.text = excerpt;
      await navigator.share(shareData);
      trackShare("native_share", trackId, title);
    } catch {
      // User cancelled - silent fail
    }
  };

  return (
    <section
      aria-label="Share this article"
      className="mt-16 -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-16 px-4 sm:px-6 lg:px-8 xl:px-16
        pt-8 pb-6 bg-slate-900 rounded-t-2xl"
    >
      {/* Header row - compact single line */}
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2" dir={arabic ? "rtl" : "ltr"}>
          <Share2 className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-400">
            {arabic ? "شارك هذا المقال" : "Share this article"}
          </p>
        </div>
      </div>

      {/* Icon-only share buttons - compact circles, mobile-first */}
      <div className="flex items-center gap-2.5">

        {/* LinkedIn */}
        <a
          href={linkedInUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
          title="Share on LinkedIn"
          onClick={() => trackShare("linkedin", trackId, title)}
          className="w-9 h-9 rounded-full flex items-center justify-center
            bg-[#0A66C2] hover:bg-[#004182] text-white
            transition-all duration-200 active:scale-90
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A66C2]"
        >
          <Linkedin className="w-4 h-4" />
        </a>

        {/* X / Twitter */}
        <a
          href={twitterUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
          title="Share on X / Twitter"
          onClick={() => trackShare("x_twitter", trackId, title)}
          className="w-9 h-9 rounded-full flex items-center justify-center
            bg-black hover:bg-zinc-700 text-white border border-white/20
            transition-all duration-200 active:scale-90
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
        >
          <Twitter className="w-4 h-4" />
        </a>

        {/* WhatsApp */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on WhatsApp"
          title="Share on WhatsApp"
          onClick={() => trackShare("whatsapp", trackId, title)}
          className="w-9 h-9 rounded-full flex items-center justify-center
            bg-[#25D366] hover:bg-[#1da851] text-white
            transition-all duration-200 active:scale-90
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
        >
          <WhatsAppIcon className="w-4 h-4" />
        </a>

        {/* Copy link */}
        <CopyLinkBtn url={canonicalUrl} slug={trackId} title={title} />

        {/* Thin divider */}
        <span className="w-px h-6 bg-white/15 mx-0.5" aria-hidden="true" />

        {/* Follow Modi on LinkedIn - outlined circle */}
        <a
          href="https://www.linkedin.com/in/modielnadi/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Follow Modi Elnadi on LinkedIn"
          title="Follow Modi on LinkedIn"
          onClick={() => trackShare("linkedin_follow", trackId, title)}
          className="w-9 h-9 rounded-full flex items-center justify-center
            border border-[#0A66C2]/50 hover:border-[#0A66C2] hover:bg-[#0A66C2]/20
            text-[#4d9de0] hover:text-white
            transition-all duration-200 active:scale-90
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A66C2]"
        >
          <Linkedin className="w-4 h-4" />
        </a>

        {/* Google Preferred Source */}
        <div
          className="flex items-center"
          onClick={() => trackShare("google_preferred_source", trackId, title)}
        >
          <GooglePreferredSourceButton variant="dark" className="flex-shrink-0 scale-90 origin-left" />
        </div>

        {/* Native share - mobile only */}
        {hasNativeShare && (
          <button
            onClick={handleNativeShare}
            aria-label="More sharing options"
            title="More sharing options"
            className="sm:hidden w-9 h-9 rounded-full flex items-center justify-center
              border border-white/20 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white
              transition-all duration-200 active:scale-90"
          >
            <Share2 className="w-4 h-4" />
          </button>
        )}
      </div>

            {/* Absolute hero image URL hint for debugging - hidden from UI */}
      {absoluteHeroImage && (
        <meta itemProp="image" content={absoluteHeroImage} />
      )}

      {/* Thin separator that visually connects to RelatedPosts below */}
      <div className="mt-8 border-t border-white/10" />
    </section>
  );
}
